import { IFRAME_MESSAGE_TYPE, IFrameMessageType, sendIframeMessage } from '@shared/iframe-messages'
import { uuid } from '@shared/util/uuid'

export interface StateApiOptions {
  /** Override the default page-level scope. Used by web components with persist-key or nested artifacts. */
  scope?: string
}

export interface StateApi {
  get: <T = unknown>(key: string, options?: StateApiOptions) => Promise<T | null>
  set: (key: string, value: unknown, options?: StateApiOptions) => Promise<void>
  remove: (key: string, options?: StateApiOptions) => Promise<void>
  clear: (options?: StateApiOptions) => Promise<void>
  getAll: (options?: StateApiOptions) => Promise<Record<string, unknown>>
}

function waitForResponse<T>(requestUuid: string, timeoutMs = 10_000): Promise<T> {
  return new Promise((resolve, reject) => {
    let timer: ReturnType<typeof setTimeout>
    const handler = (event: MessageEvent) => {
      if (typeof event.data !== 'string')
        return
      let data: any
      try {
        data = JSON.parse(event.data)
      }
      // eslint-disable-next-line unused-imports/no-unused-vars
      catch (_e) {
        return
      }
      if (
        data.type === IFRAME_MESSAGE_TYPE
        && data.action === IFrameMessageType.OnStateResult
        && data.uuid === requestUuid
      ) {
        clearTimeout(timer)
        window.removeEventListener('message', handler)
        resolve(data.value as T)
      }
    }
    window.addEventListener('message', handler)
    timer = setTimeout(() => {
      window.removeEventListener('message', handler)
      reject(new Error(`State request ${requestUuid} timed out after ${timeoutMs}ms`))
    }, timeoutMs)
  })
}

export function createStateAPI(): StateApi {
  return {
    async get<T = unknown>(key: string, options?: StateApiOptions): Promise<T | null> {
      const id = uuid()
      sendIframeMessage(IFrameMessageType.CmdStateGet, { uuid: id, key, scope: options?.scope })
      return waitForResponse<T | null>(id)
    },
    async set(key: string, value: unknown, options?: StateApiOptions): Promise<void> {
      sendIframeMessage(IFrameMessageType.CmdStateSet, { key, value, scope: options?.scope })
    },
    async remove(key: string, options?: StateApiOptions): Promise<void> {
      sendIframeMessage(IFrameMessageType.CmdStateRemove, { key, scope: options?.scope })
    },
    async clear(options?: StateApiOptions): Promise<void> {
      sendIframeMessage(IFrameMessageType.CmdStateClear, { scope: options?.scope })
    },
    async getAll(options?: StateApiOptions): Promise<Record<string, unknown>> {
      const id = uuid()
      sendIframeMessage(IFrameMessageType.CmdStateGetAll, { uuid: id, scope: options?.scope })
      return waitForResponse<Record<string, unknown>>(id)
    },
  }
}
